/**
 * Display helpers shared by the Tracks panel, the track Entity Details
 * sections and the detection-side membership line (ADR-041 D-5; REQ-UI-170 /
 * -174 / -175).
 *
 * Pure functions over the client's :type:`ObjectTrackRecord` / the wire
 * :type:`TrackSummary`, so the three surfaces cannot disagree on how a
 * duration, a head time or a member count is written.
 */
import { haversineDistanceM } from "@/map/trail-eligibility";
import {
  type ObjectTrackRecord,
  type TrackMember,
  type TrackSummary,
  firstTimeMs,
  headTimeMs,
  memberCount,
} from "@/object-tracks/object-track-store";

/** ``m:ss`` (or ``h:mm:ss`` past an hour) for a span in seconds; ``—`` when unknown. */
export function formatDuration(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) {
    return "—";
  }
  const whole = Math.floor(seconds);
  const h = Math.floor(whole / 3600);
  const m = Math.floor((whole % 3600) / 60);
  const s = whole % 60;
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  return `${h > 0 ? `${h}:` : ""}${mm}:${String(s).padStart(2, "0")}`;
}

/**
 * One shared ``Intl.DateTimeFormat`` for the track rows' wall clock — the same
 * reason as ``detections/detection-display.ts``: ``toLocaleTimeString`` with an
 * options object builds a formatter per call, and the Tracks panel calls this
 * once per row and once per expanded member on every render (feature spec
 * ``ui-track-load-headroom`` §2.2). Byte-identical output.
 */
const TRACK_CLOCK_FORMAT = new Intl.DateTimeFormat(undefined, {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

/** Compact wall-clock (HH:MM:SS) for the timeline rows (the Detections panel's). */
export function formatTrackClock(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return TRACK_CLOCK_FORMAT.format(parsed);
}

/** ``×N`` — the member-count badge text. */
export function formatMemberCount(count: number): string {
  return `×${count}`;
}

/**
 * The span a track covers in seconds — from the summary's first member to the
 * head, else from the client's own members. ``null`` when neither is known.
 */
export function trackDurationSeconds(record: ObjectTrackRecord): number | null {
  const head = headTimeMs(record);
  const first = firstTimeMs(record);
  if (!Number.isFinite(head) || !Number.isFinite(first)) {
    return null;
  }
  return Math.max(0, (head - first) / 1000);
}

/** The head's primary class: the newest member's, else the summary's. */
export function trackPrimaryClass(record: ObjectTrackRecord): string | null {
  const head = record.members.length > 0 ? record.members[record.members.length - 1] : null;
  return head?.primary_class ?? record.summary?.primary_class ?? null;
}

/**
 * Whether the track has any unpositioned member (REQ-UI-071 semantics carried
 * up): the summary's counts when known, else the held members.
 */
export function trackHasApproximateMember(record: ObjectTrackRecord): boolean {
  if (record.summary !== null) {
    return record.summary.positioned_count < record.summary.detection_count;
  }
  return record.members.some((m) => m.latitude_deg === null || m.longitude_deg === null);
}

/** Whether "Select on Map" can land somewhere: at least one positioned member. */
export function trackIsLocatable(record: ObjectTrackRecord): boolean {
  if (record.summary !== null && record.summary.positioned_count > 0) {
    return true;
  }
  return record.members.some((m) => m.latitude_deg !== null && m.longitude_deg !== null);
}

/** A summary's envelope as four numbers, or ``null`` when no member is positioned (REQ-STORAGE-059). */
export function summaryEnvelope(
  summary: TrackSummary,
): { minLon: number; minLat: number; maxLon: number; maxLat: number } | null {
  if (
    summary.bbox_min_lon === null ||
    summary.bbox_min_lat === null ||
    summary.bbox_max_lon === null ||
    summary.bbox_max_lat === null
  ) {
    return null;
  }
  return {
    minLon: summary.bbox_min_lon,
    minLat: summary.bbox_min_lat,
    maxLon: summary.bbox_max_lon,
    maxLat: summary.bbox_max_lat,
  };
}

/** The ``×N · m:ss`` membership line for a summary (REQ-UI-175). */
export function formatMembership(summary: TrackSummary): string {
  return `${formatMemberCount(summary.detection_count)} · ${formatDuration(summary.duration_seconds)}`;
}

/** The same line from a buffered record — the live count and span (REQ-UI-175). */
export function formatMembershipFromRecord(record: ObjectTrackRecord): string {
  return `${formatMemberCount(memberCount(record))} · ${formatDuration(trackDurationSeconds(record))}`;
}

/** The count the badge shows for a record — see :func:`memberCount`. */
export function trackMemberCount(record: ObjectTrackRecord): number {
  return memberCount(record);
}

/**
 * Great-circle length of the positioned member series, in metres — the sum of
 * the hops between consecutive *positioned* members (an unpositioned member
 * neither adds a hop nor breaks the chain, exactly as it draws no vertex).
 */
export function trackPathLengthM(members: readonly TrackMember[]): number {
  const points = members.filter((m) => m.latitude_deg !== null && m.longitude_deg !== null);
  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    total += haversineDistanceM(
      {
        latitude_deg: points[i - 1].latitude_deg as number,
        longitude_deg: points[i - 1].longitude_deg as number,
      },
      {
        latitude_deg: points[i].latitude_deg as number,
        longitude_deg: points[i].longitude_deg as number,
      },
    );
  }
  return total;
}

/**
 * Member detections shown in an expanded row (REQ-UI-187).
 *
 * The expansion is a **quick look at recent activity**, not the track's
 * ordered history. Before 2026-10-05 it listed every held member, so a
 * long track — the live buffer holds up to ``TRACK_MAX_MEMBERS`` = 500,
 * and a stored track can run to six figures — pushed every other track
 * off the screen, which is the one thing a list must not do to itself.
 *
 * Ten is about a screenful beneath a row and enough to read the last
 * minute of a moving object at a typical status interval. The full
 * ordered set lives in Selection Details, which pages it; the overflow
 * indicator is a link there rather than a dead end.
 */
export const EXPANDED_MEMBER_LIMIT = 10;

/**
 * The members an expanded row shows: at most
 * :data:`EXPANDED_MEMBER_LIMIT`, ending at the selected member when one is
 * past the cap.
 *
 * # Why it is a window and not just the newest N
 *
 * REQ-UI-180 makes the panel expand a track whose detection is selected
 * and scroll **that member row** into view — the thing that lands an
 * operator on the same detection inside its track after a "Select on Map"
 * from the Detections panel, or a map ring on a mid-track point. A flat
 * ``slice(0, 10)`` would silently drop that row for any selection deeper
 * than ten back, so the expansion would open and the operator would be
 * looking at the wrong ten detections with nothing highlighted.
 *
 * So the window slides: it ends at the selected member, putting the nine
 * newer ones above it for context. With nothing selected — or a selection
 * already inside the newest ten — it is exactly the newest ten.
 *
 * @param newestFirst - The track's members, newest first.
 * @param selectedMemberId - The selected member's ``report_id``, or ``null``.
 * @returns The window, newest first, never longer than the cap.
 */
export function memberWindow(
  newestFirst: readonly TrackMember[],
  selectedMemberId: string | null,
): readonly TrackMember[] {
  const index =
    selectedMemberId === null
      ? -1
      : newestFirst.findIndex((member) => member.report_id === selectedMemberId);
  const start = index < EXPANDED_MEMBER_LIMIT ? 0 : index - EXPANDED_MEMBER_LIMIT + 1;
  return newestFirst.slice(start, start + EXPANDED_MEMBER_LIMIT);
}
